import {
  CookieOptions,
  ICacheStorage,
  ITransactionStorage,
  RuleEntry,
  SimpleStorage,
  StorageManagerRules,
  StorageProvider,
  StorageUtils,
} from './storage.types';
import { storageUtils } from './storage.utils';
import { SSO_STORAGE_KEYS } from './storage.const';
import { StoredObject } from './stored-object';
import { ITokenStorage } from '../token-manager/token-manager.types';

const defaultRules: StorageManagerRules = {
  token: {
    storageTypes: ['localStorage', 'sessionStorage', 'cookie', 'memory'],
  },
  cache: {
    storageTypes: ['localStorage', 'sessionStorage', 'memory'],
  },
  transaction: {
    storageTypes: ['sessionStorage', 'cookie'],
  },
};

/**
 * StorageManager is a utility class for creating and retrieving storage interfaces.
 * Because the SDK supports multiple storage types, this class is used to abstract
 * storage implementation details.
 *
 * Given a set of rules, it will return the preferred storage method for each storage type
 * abstracted behind a common StorageProvider interface.
 *
 */
export class StorageManager {
  private readonly rules: StorageManagerRules;
  private readonly utils: StorageUtils;
  private readonly cookieOptions: CookieOptions;
  constructor(
    rules: Partial<StorageManagerRules> = StorageManager.DEFAULT_RULES,
    cookieOptions: CookieOptions = {},
    utils: StorageUtils = storageUtils,
  ) {
    this.rules = mergeRules(rules);
    this.cookieOptions = cookieOptions;
    this.utils = utils;
  }

  static DEFAULT_RULES = defaultRules;

  private getOptionsForSection(section: keyof StorageManagerRules): RuleEntry {
    return this.rules[section];
  }

  private getOptionsForRuleset({ storageProvider, storageTypes, storageType }: RuleEntry): SimpleStorage {
    const cookieOptions = Object.assign({}, this.cookieOptions);
    // They have a custom storage implementation
    if (storageProvider) {
      return storageProvider;
    }

    if (storageType === 'sessionStorage') {
      cookieOptions.sessionCookie = true;
    }
    // prioritize storageType over storageTypes
    // if storageType matches an entry in storageTypes, subsequent entries can be used as fallback
    // if storageType is not found in storageTypes, it will be used without a fallback
    if (storageType && storageTypes) {
      const index = storageTypes.indexOf(storageType);
      if (index >= 0) {
        storageTypes = storageTypes.slice(index);
        storageType = undefined;
      }
    }

    if (!storageType) {
      storageType = this.utils.findStorageType(storageTypes ?? []);
    }
    return this.utils.getStorageByType(storageType, cookieOptions);
  }

  /**
   * Returns a StorageProvider for the token storage type.
   */
  getTokenStorage(): StorageProvider<ITokenStorage> {
    const options = this.getOptionsForSection('token');
    const storage = this.getOptionsForRuleset(options);
    return new StoredObject<ITokenStorage>(storage, SSO_STORAGE_KEYS.TOKENS);
  }

  /**
   * Returns a StorageProvider for the cache storage type.
   */
  getCacheStorage(): StorageProvider<ICacheStorage> {
    const options = this.getOptionsForSection('cache');
    const storage = this.getOptionsForRuleset(options);
    return new StoredObject(storage, SSO_STORAGE_KEYS.CACHE);
  }

  /**
   * Returns a StorageProvider for the transaction storage type.
   */
  getTransactionStorage(): StorageProvider<ITransactionStorage> {
    const options = this.getOptionsForSection('transaction');
    const storage = this.getOptionsForRuleset(options);
    return new StoredObject(storage, SSO_STORAGE_KEYS.TRANSACTION);
  }
}

/**
 * Merge the default rules with any overrides
 * @param rules
 */
function mergeRules(rules: Partial<StorageManagerRules>): StorageManagerRules {
  return Object.fromEntries(
    Object.entries(defaultRules).map(([ruleKey, ruleDef]) => {
      const overrides = rules[ruleKey as keyof StorageManagerRules];
      return [ruleKey, { ...ruleDef, ...overrides }];
    }),
  ) as StorageManagerRules;
}
